import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { clsx } from 'clsx';
import Checkbox from 'components/Checkbox/Checkbox';
import DataSetsItemButtons from 'components/DataSetsItemButtons/DataSetsItemButtons';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';
import { MILLION } from 'constants/dataSets';
import { SELECTED_ITEMS } from 'constants/formFields';
import formattedData from 'utils/formattedData';
import formattedNumberWithComma from 'utils/formattedNumberWithComma';
import getMultiLabels from 'utils/getMultiLabels';
import getNaceLabel from 'utils/getNaceLabel';

import type { IOwnOperationsItem } from './interfaces/IOwnOperationsItem';

import styles from './scss/OwnOperationsItem.module.scss';

function OwnOperationsItem({
  item, control, onArchiveModalInfoOpen, onMoveToArchive, onDeArchive, onEditRecord, onDeleteRecord, isActionsVisible,
  naceCodeTreeData, locationsCountries,
}: IOwnOperationsItem): React.ReactElement {
  const { t } = useTranslation();
  const {
    id, name, valueChainSection, legalName,
    archived, latestArchivation, uniqueIdentifier, profitCenterIds, ownership, equityShareholding,
    financialControl, assetCategory, ownedLeased, numberOfEmployees, dateOpened, dateClosed,
    naceSection, naceCode, euTaxonomyCode, netBookValue, addressCountry, addressState, addressPostCode, addressCity,
    addressStreet, addressBuilding, addressCoordinates, startDate, endDate,
  } = item;

  const { naceCodeLabel, naceSectionLabel } = getNaceLabel(
    { naceCode, naceSection, naceCodeTreeData },
  );

  const hyphen = t('common.hyphen');

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  return (
    <TableRow className={clsx(undefined, { [styles.archived]: archived })}>
      <TableCell>
        <div className={styles.staticCell}>
          {archived ? (
            <span>
              {name}
              {latestArchivation ? (
                <InfoIcon
                  onClick={() => onArchiveModalInfoOpen(latestArchivation)}
                />
              ) : null}
            </span>
          ) : null}

          {!archived && (
            isActionsVisible ? (
              <Controller
                control={control}
                render={({ field: { onChange, value, disabled } }) => (
                  <Checkbox
                    key={id}
                    name={`${SELECTED_ITEMS}-${id}`}
                    checked={!!value.includes(id)}
                    value={id}
                    disabled={disabled}
                    onChange={(event) => {
                      onChange(event.target.checked
                        ? [...value, id]
                        : value.filter((selectedItem) => selectedItem !== id));
                    }}
                    label={(<span>{name}</span>)}
                  />
                )}
                name={SELECTED_ITEMS}
              />
            ) : (<span>{name}</span>)
          )}
        </div>
      </TableCell>
      <TableCell>
        {uniqueIdentifier || hyphen}
      </TableCell>
      <TableCell>
        {legalName || hyphen}
      </TableCell>
      <TableCell>
        {valueChainSection || hyphen}
      </TableCell>
      <TableCell>
        {ownership || hyphen}
      </TableCell>
      <TableCell>
        { equityShareholding ? `${formattedNumberWithComma(equityShareholding)} %` : hyphen}
      </TableCell>
      <TableCell>
        { financialControl ? `${formattedNumberWithComma(financialControl)} %` : hyphen}
      </TableCell>
      <TableCell>
        {assetCategory ? getMultiLabels({ values: assetCategory }) : hyphen}
      </TableCell>
      <TableCell>
        {ownedLeased || hyphen}
      </TableCell>
      <TableCell>
        {formattedNumberWithComma(numberOfEmployees)}
      </TableCell>
      <TableCell>
        {formattedData(dateOpened) || hyphen}
      </TableCell>
      <TableCell>
        {formattedData(dateClosed) || hyphen}
      </TableCell>
      <TableCell>
        {naceSectionLabel || hyphen}
      </TableCell>
      <TableCell>
        {naceCodeLabel || hyphen}
      </TableCell>
      <TableCell>
        {euTaxonomyCode || hyphen}
      </TableCell>
      <TableCell>
        {profitCenterIds ? getMultiLabels({ values: profitCenterIds }) : hyphen}
      </TableCell>
      <TableCell>
        {formattedData(startDate) || hyphen}
      </TableCell>
      <TableCell>
        {formattedData(endDate) || hyphen}
      </TableCell>
      <TableCell>
        {netBookValue?.amount ? `${formattedNumberWithComma(netBookValue.amount / MILLION)} M ${netBookValue?.currency}`
          : hyphen}
      </TableCell>
      <TableCell>
        {locationCountry(addressCountry) || hyphen}
      </TableCell>
      <TableCell>
        {addressState || hyphen}
      </TableCell>
      <TableCell>
        {addressCity || hyphen}
      </TableCell>
      <TableCell>
        {addressPostCode || hyphen}
      </TableCell>
      <TableCell>
        {addressStreet || hyphen}
      </TableCell>
      <TableCell>
        {addressBuilding || hyphen}
      </TableCell>
      <TableCell>
        {addressCoordinates || hyphen}
      </TableCell>
      {isActionsVisible ? (
        <TableCell>
          <DataSetsItemButtons
            id={id}
            archived={archived}
            onEditRecord={onEditRecord}
            onMoveToArchive={onMoveToArchive}
            onDeArchive={onDeArchive}
            onDeleteRecord={onDeleteRecord}
          />
        </TableCell>
      ) : null}
    </TableRow>
  );
}

export default OwnOperationsItem;
